import { Injectable, Injector, NgZone } from '@angular/core';
import { OrderformService } from './orderform.service';
import { lazyLoadComponent } from '../misc/component-loader';
import { OrderformFascade } from '../fascades/orderform.fascade';
import { switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { GenericOptionListItem } from '@ds24/ds-generic-api';
import { OrderformIframeService } from './orderform-iframe.service';
import { Store } from '@ngrx/store';
import { GrowthBookService } from './growthbook.service';
import { isPageBuilderElementVisible } from '../misc/pagebuilder';

function getViewPath() {
  const urlParts = window.location.pathname.split('/');
  return urlParts[2];
}

function getProductsURL(orderformId: number) {
  return `/v2/${getViewPath()}/api/pgb-orderform/${orderformId}/products`;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private _orderformService: OrderformService,
    private _orderformFacadeService: OrderformFascade,
    private _orderformIframe: OrderformIframeService,
    private _httpClient: HttpClient,
    private _injector: Injector,
    public store: Store,
    private _growthBookService: GrowthBookService,
    private _ngZone: NgZone
  ) {}

  // Expose formValidation for Vue components
  get formValidation() {
    return this._orderformFacadeService.formValidation;
  }

  // Expose GrowthBook service for Vue components
  get growthBook() {
    return this._growthBookService;
  }

  getOrderformProducts$(
    query: string = '',
    page: number = 1,
    maxItems: number = 100
  ): Observable<GenericOptionListItem[]> {
    return this._orderformFacadeService.config$.pipe(
      switchMap((config) => {
        const URL =
          getProductsURL(config.orderform_id) +
          '?types=vendor&query=' +
          query +
          '&page=' +
          page +
          '&itemsPerPage=' +
          maxItems;
        return this._httpClient.get<GenericOptionListItem[]>(URL);
      })
    );
  }

  preloadComponent(component): Promise<void> {
    return lazyLoadComponent(component, this._injector);
  }

  /**
   * Load only data for specific product and orderform
   * @param productId
   * @param orderformId
   * @param language
   */
  getOrderformJSON(productId: number, orderformId: number, language: string) {
    return firstValueFrom(this._orderformService.getOrderformJSON(productId, orderformId, language));
  }

  getProductId() {
    try {
      // @ts-ignore
      return window.order_data.settings.config.product_id;
    } catch (e) {
      return null;
    }
  }

  getProductEditLink(productId: string) {
    return `/${getViewPath()}/account/products/edit/${productId}`;
  }

  getLocaleIso2() {
    return this._orderformService.getLocaleIso2();
  }

  getCustomPayButtonAllowedCountryList() {
    try {
      // @ts-ignore
      return window.order_data.settings.config.is_pay_button_label_changed_allowed_countries;
    } catch (e) {
      return [];
    }
  }

  scrollIntoView(element: HTMLElement) {
    if (this._orderformIframe.iframeActive) {
      this._orderformIframe.scrollToIframeElementPosition(element.getBoundingClientRect().top);
    } else {
      element.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  /**
   * Scrolls to the first visible form validation error.
   * Handles both regular page and iframe scenarios.
   */
  async scrollToFormError(): Promise<void> {
    await new Promise<void>((resolve) => {
      this._ngZone.runOutsideAngular(() =>
        setTimeout(async () => {
          const errorElements = document.querySelectorAll(
            'orderform-input-errors .error, ds-orderform-buy-button .error, #paymentMethodInput .error-container, #paymentMethodInput .error.alert-danger'
          );
          if (!errorElements) {
            resolve();
          }

          const visibleErrors = Array.from(errorElements).filter((element) =>
            isPageBuilderElementVisible(element as HTMLElement)
          );

          // Try scrolling to each error element until successful
          // Needed for elements that exist twice (desktop/mobile) and only one is visible
          for (const errorElement of visibleErrors) {
            const beforeScroll = window.scrollY;

            errorElement.scrollIntoView({ block: 'center' });
            this._orderformIframe.scrollToIframeElementPosition(errorElement.getBoundingClientRect().top);

            // Wait for scroll animation
            await new Promise((r) => setTimeout(r, 100));

            const afterScroll = window.scrollY;
            const hasScrolled = Math.abs(afterScroll - beforeScroll) > 5;

            if (hasScrolled) {
              break;
            }
          }

          resolve();
        }, 100)
      );
    });
  }

  async validateOnlyChildComponents(element: HTMLElement) {
    const validationResult = await this._orderformFacadeService.formValidation.emit(undefined, (el) => {
      // Add null check for Vue components that might not have nativeElement
      return el && el.nativeElement && element.contains(el.nativeElement);
    });

    return validationResult.reduce((valid, result) => {
      return valid && result.valid;
    }, true);
  }
}
